import styled from 'styled-components';

import { SearchMIcon } from 'assets/icons';
import { ListItem } from 'components/uikit/Form/ListItem/ListItem';
import { TextInput } from 'components/uikit/Form/TextInput/TextInput';
import { IconButton } from 'components/uikit/IconButton/IconButton';
import { Icon } from 'components/uikit/Icon/Icon';

const DEFAULT_THEME = 1;
const DEFAULT_SIZE = 'm';

const themes = {
  1: {
    colorBkg: 'var(--color-grey-10_10)',
    colorBkgError: 'var(--color-red-4)',
    colorBkgHover: 'var(--color-grey-10_80)',
    colorBorder: 'transparent',
    colorBorderHover: 'transparent',
    colorBorderFocus: 'var(--color-black)',
    colorBorderError: 'var(--color-red-2)',
    colorBkgItemSelected: 'var(--color-grey-1)',
    colorBkgItemHover: 'var(--color-grey-1)',
    colorBkgDropdown: 'var(--color-white)',
    colorPlaceholder: 'var(--color-grey-11_60)',
    colorDescription: 'var(--color-grey-11_60)',
  },
};

const sizes = {
  m: {
    font: 'var(--font-regular-15)',
    height: '44px',
    fontDescription: 'var(--font-regular-13)',
    fontPlaceholder: 'var(--font-regular-11)',
  },
};

export const Container = styled.div`
  --color-description: ${(p) => (themes[p.theme] || themes[DEFAULT_THEME]).colorDescription};
  --color-bkg: ${(p) => (themes[p.theme] || themes[DEFAULT_THEME]).colorBkg};
  --color-bkg-hover: ${(p) => (themes[p.theme] || themes[DEFAULT_THEME]).colorBkgHover};
  --color-bkg-error: ${(p) => (themes[p.theme] || themes[DEFAULT_THEME]).colorBkgError};
  --color-border: ${(p) => (themes[p.theme] || themes[DEFAULT_THEME]).colorBorder};
  --color-border-hover: ${(p) => (themes[p.theme] || themes[DEFAULT_THEME]).colorBorderHover};
  --color-border-focus: ${(p) => (themes[p.theme] || themes[DEFAULT_THEME]).colorBorderFocus};
  --color-border-error: ${(p) => (themes[p.theme] || themes[DEFAULT_THEME]).colorBorderError};
  --color-placeholder: ${(p) => (themes[p.theme] || themes[DEFAULT_THEME]).colorPlaceholder};
  --font: ${(p) => (sizes[p.size] || sizes[DEFAULT_SIZE]).font};
  --font-description: ${(p) => (sizes[p.size] || sizes[DEFAULT_SIZE]).fontDescription};
  --font-placeholder: ${(p) => (sizes[p.size] || sizes[DEFAULT_SIZE]).fontPlaceholder};
  --height: ${(p) => (sizes[p.size] || sizes[DEFAULT_SIZE]).height};

  position: relative;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  height: fit-content;
  ${(p) => (p.disabled ? 'opacity: 56%;' : '')}
`;

export const SelectComponent = styled.select`
  width: 100%;
  height: var(--height);
  appearance: none;
  border-radius: 12px;
  cursor: inherit;
  padding: ${(p) => (p.isSelected ? '18px 32px 6px 11px' : '12px 32px 12px 12px')};
  padding-right: ${(p) => (p.withControl ? '68px' : '32px')};
  overflow: hidden;
  text-overflow: ellipsis;
  font: var(--font);
  background: ${(p) => (p.error ? 'var(--color-bkg-error)' : 'var(--color-bkg)')};
  border: 1px solid var(--color-border);
  ${(p) =>
    p.isOpened
      ? p.error
        ? 'border-color: var(--color-border-error);'
        : 'border-color: var(--color-black);'
      : ''}
  color: ${(p) => (!p.isSelected ? `var(--color-placeholder)` : 'inherit')};

  :hover:not([disabled]) {
    border-color: ${(p) =>
      p.error
        ? 'var(--color-border-error)'
        : p.isOpened
        ? 'var(--color-border-focus)'
        : 'var(--color-border-hover)'};
    background-color: ${(p) => (p.error ? 'var(--color-bkg-error)' : 'var(--color-bkg-hover)')};
  }

  &:focus {
    border: 1px solid
      ${(p) => (p.error ? 'var(--color-border-error)' : 'var(--color-border-focus)')};
  }
`;

export const SearchInput = styled(TextInput).attrs({ theme: 2, Icon: SearchMIcon })`
  margin: 10px 12px;
`;

export const ListItemStyled = styled(ListItem)`
  cursor: pointer;
  border-radius: 12px;
  padding: 10px 12px;

  ${(p) => (p.selected ? `background: var(--color-bkg-item-selected);` : '')}

  &:hover {
    background: var(--color-bkg-item-hover);
  }
`;

export const OptionItem = styled.option`
  font: var(--font);
  display: ${(p) => (p.visible ? 'block' : 'none')};
`;

export const Placeholder = styled.div`
  position: absolute;
  top: 6px;
  left: 12px;
  font: var(--font-placeholder);
  color: var(--color-placeholder);
`;

export const DropDownContainer = styled.div`
  --color-bkg-dropdown: ${(p) => (themes[p.theme] || themes[DEFAULT_THEME]).colorBkgDropdown};
  --color-bkg-item-selected: ${(p) =>
    (themes[p.theme] || themes[DEFAULT_THEME]).colorBkgItemSelected};
  --color-bkg-item-hover: ${(p) => (themes[p.theme] || themes[DEFAULT_THEME]).colorBkgItemHover};

  background: var(--color-bkg-dropdown);
  border-radius: 12px;
  box-shadow: 0 4px 32px var(--color-grey-3);
  padding: 4px;
  max-height: 378px;
  overflow-y: auto;
  z-index: 9999999999;
`;

export const Chevron = styled(IconButton)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 10px;
  width: 24px;
  height: 24px;
  z-index: 999;
  cursor: inherit;
  --color-icon: var(--color-grey-11_60);
`;

export const Error = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  color: var(--color-red-2);
  font: var(--font-description);
  cursor: default;
`;

export const Description = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  font: var(--font-description);
  color: var(--color-description);
  cursor: default;
`;

export const EmptyIcon = styled.div`
  width: 24px;
`;

export const CheckIcon = styled(Icon)`
  --color: var(--color-black);
`;

export const ControlWrapper = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 44px;
`;
